import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Zap, TrendingUp, AlertCircle, Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import type { Lead } from "@/types/propostas";

interface Step2DimensionamentoSistemaProps {
  onBack: () => void;
  onNext: (dimensionamentoId?: string) => void;
  selectedLead: Lead | null; // Dados do lead selecionado da etapa 1
  dimensionamentoId?: string | null; // ID do dimensionamento selecionado
  readOnly?: boolean;
}

interface DimensionamentoResult {
  systemPowerKWp: string;
  annualGenPerKwp: string;
  monthlyGeneration?: { [key: string]: number };
}

interface IrradiationData {
  Jan: string | number;
  Fev: string | number;
  Mar: string | number;
  Abr: string | number;
  Mai: string | number;
  Jun: string | number;
  Jul: string | number;
  Ago: string | number;
  Set: string | number;
  Out: string | number;
  Nov: string | number;
  Dez: string | number;
  Média: string | number;
}

interface ConsumoMedioState {
  mediaConsumo?: number;
  mediaConsumoPonta?: number;
  mediaConsumoForaPonta?: number;
  dadosMensais: DadosMensaisItem[];
}

interface DadosMensaisItem {
  consumo?: string;
  consumo_ponta?: string;
  consumo_fora_ponta?: string;
  tusd_ponta?: string;
  te_ponta?: string;
  tusd_fora_ponta?: string;
  te_fora_ponta?: string;
  [key: string]: string | number | undefined;
}

export default function Step2DimensionamentoSistema({ 
  onBack, 
  onNext, 
  selectedLead,
  dimensionamentoId,
  readOnly = false
}: Step2DimensionamentoSistemaProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [efficiency, setEfficiency] = useState("74.0");
  const [targetPercentage, setTargetPercentage] = useState("");
  const [results, setResults] = useState<DimensionamentoResult | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [leadData, setLeadData] = useState<Lead | null>(null);
  const [isLoadingLead, setIsLoadingLead] = useState(false);
  const [consumoMedio, setConsumoMedio] = useState<ConsumoMedioState | null>(null);
  const [dimensionamentoRestaurado, setDimensionamentoRestaurado] = useState(false);
  
  // Estados para detectar mudanças
  const [originalTargetPercentage, setOriginalTargetPercentage] = useState<string | null>(null);
  const [originalEfficiency, setOriginalEfficiency] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Funções utilitárias para eliminar duplicação de código
  const getAverage = (items: DadosMensaisItem[], key: string): number => {
    const values = items
      .map(item => parseFloat(String(item[key] || '0')))
      .filter(v => v > 0);
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  const calculateConsumoEfetivo = (dadosMensais: DadosMensaisItem[]): number => {
    if (!dadosMensais || !Array.isArray(dadosMensais)) return 3; // Fallback
    
    const avgTusdPonta = getAverage(dadosMensais, 'tusd_ponta');
    const avgTePonta = getAverage(dadosMensais, 'te_ponta');
    const avgTusdForaPonta = getAverage(dadosMensais, 'tusd_fora_ponta');
    const avgTeForaPonta = getAverage(dadosMensais, 'te_fora_ponta');

    const pontaSum = avgTusdPonta + avgTePonta;
    const foraPontaSum = avgTusdForaPonta + avgTeForaPonta;

    return foraPontaSum > 0 ? pontaSum / foraPontaSum : 3; // Fallback para 3
  };

  const getMinKwhByPadrao = (padrao: string, faturaMinimaKwh?: number | null): number => {
    if (padrao === 'Múltiplas Faturas') {
      const v = faturaMinimaKwh != null ? Number(faturaMinimaKwh) : NaN;
      if (typeof v !== 'number' || Number.isNaN(v) || v <= 0) {
        throw new Error('Para padrão Múltiplas Faturas, informe a Fatura Mínima (kWh) na etapa 1 (Informações do Cliente).');
      }
      return v;
    }
    const minKwhMap: { [key: string]: number } = {
      'Monofásico': 30,
      'Bifásico': 50,
      'Trifásico': 100
    };
    return minKwhMap[padrao] ?? 100;
  };

  // Função para carregar dados de consumo médio baseado no grupo tarifário
  const loadConsumoMedio = useCallback(async (lead: Lead) => {
    if (!lead.grupo_tarifario) return;

    try {
      let targetTable;
      switch (lead.grupo_tarifario) {
        case 'Grupo A':
          targetTable = 'clientes_grupoa';
          break;
        case 'Grupo B':
          targetTable = 'clientes_grupob';
          break;
        case 'Mercado Livre':
          targetTable = 'clientes_ml';
          break;
        default:
          return;
      }

      const monthlyResponse = await fetchWithAuth(`/api/leads/${lead.id}/dimensionamento?grupo=${targetTable}&dimensionamento_id=${dimensionamentoId}`);
      
      if (monthlyResponse.ok) {
        const monthlyResult = await monthlyResponse.json();
        
        // Carregar dados de dimensionamento salvos anteriormente
        if (monthlyResult.dimensio || monthlyResult.percentual || monthlyResult.eficiencia || monthlyResult.geracao_anual_kwp) {
          console.log('🔄 Dados de dimensionamento encontrados:', monthlyResult);
          
          // Restaurar valores dos campos
          if (monthlyResult.dimensio) {
            setResults({
              systemPowerKWp: monthlyResult.dimensio.toString(),
              annualGenPerKwp: monthlyResult.geracao_anual_kwp ? monthlyResult.geracao_anual_kwp.toString() : '0'
            });
            setIsCalculated(true);
          }
          
          if (monthlyResult.percentual) {
            const percentualValue = monthlyResult.percentual.toString();
            setTargetPercentage(percentualValue);
            setOriginalTargetPercentage(percentualValue); // Definir valor original
          }
          
          if (monthlyResult.eficiencia) {
            const eficienciaValue = monthlyResult.eficiencia.toString();
            setEfficiency(eficienciaValue);
            setOriginalEfficiency(eficienciaValue); // Definir valor original
          }
          
          // Marcar como salvo se todos os dados estiverem presentes
          if (monthlyResult.dimensio && monthlyResult.percentual && monthlyResult.eficiencia && monthlyResult.geracao_anual_kwp) {
            setIsSaved(true);
            setDimensionamentoRestaurado(true);
            console.log('✅ Dimensionamento restaurado com sucesso');
          }
        }
        
        if (monthlyResult.dados_mensais && Array.isArray(monthlyResult.dados_mensais)) {
          let mediaConsumo: number | undefined;
          let mediaConsumoPonta: number | undefined;
          let mediaConsumoForaPonta: number | undefined;

          if (lead.grupo_tarifario === 'Grupo B') {
            // Para Grupo B: calcular média de consumo mensal
            const consumos = monthlyResult.dados_mensais
              .filter((item: DadosMensaisItem) => item.consumo && parseFloat(item.consumo) > 0)
              .map((item: DadosMensaisItem) => parseFloat(item.consumo));

            if (consumos.length > 0) {
              mediaConsumo = consumos.reduce((sum, val) => sum + val, 0) / consumos.length;
            }
          } else {
            // Para Grupo A e Mercado Livre: calcular médias de ponta e fora ponta
            const consumosPonta = monthlyResult.dados_mensais
              .map((item: DadosMensaisItem) => parseFloat(item.consumo_ponta || '0'))
              .filter((val: number) => val > 0);

            const consumosForaPonta = monthlyResult.dados_mensais
              .map((item: DadosMensaisItem) => parseFloat(item.consumo_fora_ponta || '0'))
              .filter((val: number) => val > 0);

            if (consumosPonta.length > 0) {
              mediaConsumoPonta = consumosPonta.reduce((sum, val) => sum + val, 0) / consumosPonta.length;
            }

            if (consumosForaPonta.length > 0) {
              mediaConsumoForaPonta = consumosForaPonta.reduce((sum, val) => sum + val, 0) / consumosForaPonta.length;
            }
          }

          setConsumoMedio({
            mediaConsumo,
            mediaConsumoPonta,
            mediaConsumoForaPonta,
            dadosMensais: monthlyResult.dados_mensais as DadosMensaisItem[]
          });

          console.log('✅ Dados de consumo médio carregados:', {
            mediaConsumo,
            mediaConsumoPonta,
            mediaConsumoForaPonta
          });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados de consumo médio:', error);
    }
  }, []);

  // Função para carregar dados atualizados do lead
  const loadLeadData = useCallback(async () => {
    if (!selectedLead?.id) return;
    
    setIsLoadingLead(true);
    try {
      const response = await fetchWithAuth(`/api/leads/${selectedLead.id}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar dados do lead');
      }
      
      const data = await response.json();
      setLeadData(data);
      
      console.log('✅ Dados do lead carregados na etapa 2:', data);
      console.log('✅ Grupo tarifário:', data.grupo_tarifario);
      console.log('✅ Padrão:', data.padrao);
      
      // Carregar dados de consumo médio
      await loadConsumoMedio(data);
      
    } catch (error) {
      console.error('Erro ao carregar dados do lead:', error);
      toast({
        title: "Erro ao carregar dados do cliente",
        description: "Não foi possível carregar as informações do cliente. Verifique a conexão.",
        variant: "destructive"
      });
    } finally {
      setIsLoadingLead(false);
    }
  }, [selectedLead?.id, loadConsumoMedio, toast]);

  // Carregar dados atualizados do lead quando o componente for montado
  useEffect(() => {
    if (selectedLead?.id) {
      loadLeadData();
    }
  }, [selectedLead?.id, loadLeadData]);

  // Função para obter dados de irradiação da localidade do cliente
  const getIrradiation = async (state: string, city: string) => {
    try {
      // Buscar cidade na tabela de municípios
      const { data: cityData, error: cityError } = await supabase
        .from('municipios')
        .select('id')
        .eq('municipio', city)
        .eq('uf', state)
        .single();

      if (cityError || !cityData) {
        throw new Error('Município não encontrado na base de dados');
      }

      // Buscar dados de irradiação
      const { data: irradiationData, error: irradiationError } = await supabase
        .from('irradiacao')
        .select('"Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez", "Média"')
        .eq('municipio_id', cityData.id)
        .single();

      if (irradiationError || !irradiationData) {
        throw new Error('Dados de irradiação não encontrados para esta localidade');
      }

      // Processar dados de irradiação
      const parseValue = (val: string | number) => {
        if (typeof val === 'string') {
          return parseFloat(val.replace(',', '.'));
        } else if (typeof val === 'number') {
          return val;
        } else {
          return NaN;
        }
      };

      // Verificar se os dados são válidos antes de processar
      if (!irradiationData || typeof irradiationData !== 'object') {
        throw new Error('Dados de irradiação inválidos');
      }

      const months = [
        parseValue((irradiationData as IrradiationData).Jan),
        parseValue((irradiationData as IrradiationData).Fev),
        parseValue((irradiationData as IrradiationData).Mar),
        parseValue((irradiationData as IrradiationData).Abr),
        parseValue((irradiationData as IrradiationData).Mai),
        parseValue((irradiationData as IrradiationData).Jun),
        parseValue((irradiationData as IrradiationData).Jul),
        parseValue((irradiationData as IrradiationData).Ago),
        parseValue((irradiationData as IrradiationData).Set),
        parseValue((irradiationData as IrradiationData).Out),
        parseValue((irradiationData as IrradiationData).Nov),
        parseValue((irradiationData as IrradiationData).Dez)
      ];

      const media = parseValue((irradiationData as IrradiationData).Média);

      if (months.some(m => isNaN(m)) || isNaN(media)) {
        throw new Error('Dados de irradiação inválidos ou incompletos');
      }

      return { monthly: months, psh: media };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      throw new Error(`Erro ao obter dados de irradiação: ${errorMessage}`);
    }
  };

  // Função para calcular o dimensionamento do sistema usando metodologia da Calculadora
  const calculateDimensionamento = async () => {
    if (!validateInputs()) {
      return;
    }

    setIsLoading(true);
    setValidationErrors([]);

    try {
      // Verificar se temos os dados necessários do lead
      if (!leadData?.id) {
        throw new Error('Dados do lead não carregados');
      }

      // Verificar se temos os dados necessários
      if (!leadData.grupo_tarifario || !leadData.padrao || !leadData.cidade || !leadData.estado) {
        throw new Error('Dados do cliente incompletos. Complete a etapa 1 primeiro.');
      }

      console.log('🔍 Iniciando cálculo para cliente:', {
        id: leadData.id,
        grupo: leadData.grupo_tarifario,
        padrao: leadData.padrao,
        cidade: leadData.cidade,
        estado: leadData.estado
      });

      // Obter dados de irradiação
      const irradiationData = await getIrradiation(leadData.estado, leadData.cidade);
      const monthlyIrr = irradiationData.monthly;
      const psh = irradiationData.psh;
      
      console.log('✅ Dados de irradiação obtidos:', {
        psh: psh,
        monthlyCount: monthlyIrr.length
      });
      
      // Calcular eficiência
      const eficiencia = parseFloat(efficiency) / 100;
      if (isNaN(eficiencia) || eficiencia <= 0 || eficiencia > 1) {
        throw new Error('Valor de eficiência inválido');
      }

      // Calcular geração mensal e anual por kWp (metodologia da Calculadora)
      const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      const monthNames = [
        'janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];
      
      let annualGenPerKwp = 0;
      const monthlyGeneration: { [key: string]: number } = {};
      
      console.log('🔍 Debug cálculo geração mensal e anual:');
      console.log('  - Eficiência:', eficiencia);
      console.log('  - Dados de irradiação mensal:', monthlyIrr);
      console.log('  - Dias por mês:', daysPerMonth);
      
      for (let m = 0; m < 12; m++) {
        const monthlyGen = monthlyIrr[m] * daysPerMonth[m] * eficiencia;
        monthlyGeneration[monthNames[m]] = parseFloat(monthlyGen.toFixed(2));
        annualGenPerKwp += monthlyGen;
        console.log(`  - Mês ${m + 1} (${monthNames[m]}): irradiação=${monthlyIrr[m]}, dias=${daysPerMonth[m]}, geração=${monthlyGen.toFixed(2)}, acumulado=${annualGenPerKwp.toFixed(2)}`);
      }
      
      console.log('📊 Geração mensal calculada:', monthlyGeneration);

      console.log('  - Geração anual total por kWp:', annualGenPerKwp.toFixed(2));

      if (annualGenPerKwp === 0) {
        throw new Error('Geração anual calculada é zero. Verifique os dados de irradiação.');
      }

      // Calcular potência necessária baseada no grupo tarifário (metodologia da Calculadora)
      let kWp = 0;
      let targetConsumption = 0;

      if (leadData.grupo_tarifario === 'Grupo B') {
        console.log('🔍 Calculando para Grupo B...');
        // Para Grupo B, buscar dados mensais e calcular consumo efetivo
        const monthlyResponse = await fetchWithAuth(`/api/leads/${leadData.id}/dimensionamento?grupo=clientes_grupob&dimensionamento_id=${dimensionamentoId}`);
        
        if (monthlyResponse.ok) {
          const monthlyResult = await monthlyResponse.json();
          
          if (monthlyResult.dados_mensais && Array.isArray(monthlyResult.dados_mensais)) {
            // Calcular consumo médio mensal (sem considerar fatura em R$)
            const monthlyConsumptions = monthlyResult.dados_mensais
              .filter((item: DadosMensaisItem) => item.consumo && parseFloat(item.consumo) > 0)
              .map((item: DadosMensaisItem) => parseFloat(item.consumo));

            if (monthlyConsumptions.length > 0) {
              const avgMonthlyConsumption = monthlyConsumptions.reduce((sum, val) => sum + val, 0) / monthlyConsumptions.length;
              
              // Aplicar percentual desejado
              targetConsumption = avgMonthlyConsumption * (parseFloat(targetPercentage) / 100);
              
              // Aplicar a mesma lógica da Calculadora: subtrair consumo mínimo baseado no padrão
              const minKwh = getMinKwhByPadrao(leadData.padrao || '', leadData.fatura_minima_kwh);
              
              // Verificar se o consumo alvo é maior que o consumo mínimo
              if (targetConsumption <= minKwh) {
                throw new Error(`Consumo alvo (${targetConsumption.toFixed(2)} kWh) deve ser maior que o consumo mínimo (${minKwh} kWh) para o padrão ${leadData.padrao}`);
              }
              
              // Fórmula idêntica à Calculadora: annualConsAdjusted = 12 * (consumo - minKwh)
              const annualConsAdjusted = 12 * (targetConsumption - minKwh);
              
              // Logs para debug do cálculo
              console.log('🔍 Debug cálculo Grupo B:');
              console.log('  - Consumo médio mensal:', avgMonthlyConsumption);
              console.log('  - Percentual desejado:', targetPercentage);
              console.log('  - Consumo alvo:', targetConsumption);
              console.log('  - Padrão:', leadData.padrao);
              console.log('  - Consumo mínimo (kWh):', minKwh);
              console.log('  - Consumo anual ajustado:', annualConsAdjusted);
              console.log('  - Geração anual por kWp:', annualGenPerKwp);
              
              // Calcular potência necessária
              kWp = annualConsAdjusted / annualGenPerKwp;
              
              console.log('  - Potência calculada (kWp):', kWp);
            } else {
              throw new Error('Nenhum dado de consumo válido encontrado');
            }
          } else {
            throw new Error('Dados mensais não encontrados');
          }
        } else {
          throw new Error('Erro ao buscar dados mensais do cliente');
        }
      } else {
        // Para Grupo A e Mercado Livre, buscar dados mensais
        const tableName = leadData.grupo_tarifario === 'Grupo A' ? 'clientes_grupoa' : 'clientes_ml';
        console.log(`🔍 Calculando para ${leadData.grupo_tarifario} na tabela: ${tableName}`);
        
        const monthlyResponse = await fetchWithAuth(`/api/leads/${leadData.id}/dimensionamento?grupo=${tableName}&dimensionamento_id=${dimensionamentoId}`);
        
        if (monthlyResponse.ok) {
          const monthlyResult = await monthlyResponse.json();
          
          if (monthlyResult.dados_mensais && Array.isArray(monthlyResult.dados_mensais)) {
            console.log(`✅ Dados mensais encontrados para ${leadData.grupo_tarifario}:`, monthlyResult.dados_mensais.length, 'meses');
            
            // Calcular multiplicador dinâmico usando função utilitária
            const multiplier = calculateConsumoEfetivo(monthlyResult.dados_mensais);
            
            console.log('🔍 Multiplicador calculado:', {
              multiplier
            });

            // Calcular consumo efetivo mensal com o novo multiplicador
            const monthlyEffectiveConsumptions = monthlyResult.dados_mensais
              .map((item: DadosMensaisItem) => {
                const consumoForaPonta = parseFloat(item.consumo_fora_ponta || '0');
                const consumoPonta = parseFloat(item.consumo_ponta || '0');
                if (consumoForaPonta === 0 && consumoPonta === 0) return 0;
                return consumoForaPonta + (consumoPonta * multiplier);
              })
              .filter(val => val > 0);

            console.log('🔍 Consumos efetivos mensais calculados:', monthlyEffectiveConsumptions);

            if (monthlyEffectiveConsumptions.length > 0) {
              const avgMonthlyEffectiveConsumption = monthlyEffectiveConsumptions.reduce((sum, val) => sum + val, 0) / monthlyEffectiveConsumptions.length;
              
              // Aplicar percentual desejado
              targetConsumption = avgMonthlyEffectiveConsumption * (parseFloat(targetPercentage) / 100);
              
              // Calcular potência necessária
              const annualConsumption = targetConsumption * 12;
              kWp = annualConsumption / annualGenPerKwp;
              
              console.log('🔍 Cálculo final para', leadData.grupo_tarifario, ':', {
                avgMonthlyEffectiveConsumption,
                targetPercentage,
                targetConsumption,
                annualConsumption,
                annualGenPerKwp,
                kWp
              });
            } else {
              throw new Error('Nenhum dado de consumo válido encontrado');
            }
          } else {
            throw new Error('Dados mensais não encontrados');
          }
        } else {
          throw new Error('Erro ao buscar dados mensais do cliente');
        }
      }

      if (isNaN(kWp) || kWp <= 0) {
        throw new Error('Erro no cálculo da potência do sistema');
      }

      // Validar se os resultados são válidos antes de definir
      if (isNaN(annualGenPerKwp) || annualGenPerKwp <= 0) {
        throw new Error('Erro no cálculo da geração anual por kWp');
      }

      // Definir resultados (incluindo geração mensal para salvamento)
      const dimensionamentoResult: DimensionamentoResult = {
        systemPowerKWp: kWp.toFixed(2),
        annualGenPerKwp: annualGenPerKwp.toFixed(2),
        monthlyGeneration: monthlyGeneration
      };

      console.log('✅ Resultados finais do dimensionamento:', dimensionamentoResult);

      setResults(dimensionamentoResult);
      setIsCalculated(true);
      setIsSaved(false); // Novo cálculo não está salvo
      setDimensionamentoRestaurado(false); // Não é mais um dimensionamento restaurado
      
      toast({
        title: "Dimensionamento calculado com sucesso!",
        description: `Sistema de ${dimensionamentoResult.systemPowerKWp} kWp calculado para gerar ${targetPercentage}% da energia necessária.`,
        duration: 2000
      });

    } catch (error: unknown) {
      console.error('❌ Erro no dimensionamento:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      // Mensagem de erro mais específica para Grupo A
      if (leadData?.grupo_tarifario === 'Grupo A') {
        toast({
          title: "Erro no dimensionamento para Grupo A",
          description: `Problema específico: ${errorMessage}. Verifique se todos os dados estão corretos e tente novamente.`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erro no dimensionamento",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Função para limpar resultado e permitir novo cálculo
  const clearResults = () => {
    if (!results) return;
    
    setIsCalculated(false);
    setIsSaved(false);
    setDimensionamentoRestaurado(false);
    setResults(null);
    setTargetPercentage("");
    setEfficiency("74.0");
    // O usuário pode alterar as variáveis e clicar em "Calcular Dimensionamento"
  };

  // Função para salvar o resultado do dimensionamento na base de dados
  const saveDimensionamento = async () => {
    if (!results || !leadData?.id || !dimensionamentoId) {
      toast({
        title: "Erro ao salvar",
        description: "Dados do dimensionamento, do lead ou do dimensionamento não estão disponíveis.",
        variant: "destructive"
      });
      return;
    }
    
    console.log('🔍 Estado atual dos resultados:', results);
    console.log('🔍 Tipo de results.annualGenPerKwp:', typeof results.annualGenPerKwp);
    console.log('🔍 Valor de results.annualGenPerKwp:', results.annualGenPerKwp);
    console.log('🔍 Lead data:', leadData);
    
    // Verificar se há mudanças nos campos de percentual ou eficiência
    if (hasUnsavedChanges) {
      console.log('⚠️ Mudanças detectadas - limpando dados das etapas 3 e 4 antes de salvar');
      await clearEtapasSeguintes(leadData.id);
      
      // Mostrar aviso ao usuário
      toast({
        title: "Dados das etapas seguintes limpos",
        description: "Como houve alterações no dimensionamento, os dados das etapas 3 e 4 foram removidos para manter a consistência.",
        variant: "default"
      });
    }
    
    // Validação robusta antes do envio
    console.log('🔍 Validando resultados antes do envio:');
    console.log('  - systemPowerKWp:', results.systemPowerKWp);
    console.log('  - annualGenPerKwp:', results.annualGenPerKwp);
    console.log('  - monthlyGeneration:', results.monthlyGeneration);
    console.log('  - Tipo de monthlyGeneration:', typeof results.monthlyGeneration);
    
    if (!results.systemPowerKWp || !results.annualGenPerKwp || !results.monthlyGeneration) {
      console.log('❌ Validação falhou - dados incompletos');
      toast({
        title: "Dados incompletos",
        description: "Resultados do dimensionamento estão incompletos. Recalcule o sistema.",
        variant: "destructive"
      });
      return;
    }
    
    console.log('✅ Validação básica passou');
    
    // Validar se os valores são números válidos
    const systemPower = parseFloat(results.systemPowerKWp);
    const annualGen = parseFloat(results.annualGenPerKwp);
    
    if (isNaN(systemPower) || systemPower <= 0) {
      toast({
        title: "Potência inválida",
        description: "A potência do sistema deve ser um número positivo válido.",
        variant: "destructive"
      });
      return;
    }
    
    if (isNaN(annualGen) || annualGen <= 0) {
      toast({
        title: "Geração anual inválida",
        description: "A geração anual por kWp deve ser um número positivo válido.",
        variant: "destructive"
      });
      return;
    }
    
    // Validar campos obrigatórios
    if (!targetPercentage || !efficiency) {
      toast({
        title: "Campos obrigatórios",
        description: "Percentual e eficiência são obrigatórios para salvar o dimensionamento.",
        variant: "destructive"
      });
      return;
    }
    
    const percentual = parseFloat(targetPercentage);
    const eficiencia = parseFloat(efficiency);
    
    if (isNaN(percentual) || percentual <= 0) {
      toast({
        title: "Percentual inválido",
        description: "O percentual deve ser um número positivo válido.",
        variant: "destructive"
      });
      return;
    }
    
    if (isNaN(eficiencia) || eficiencia <= 0 || eficiencia > 100) {
      toast({
        title: "Eficiência inválida",
        description: "A eficiência deve ser um número entre 0 e 100.",
        variant: "destructive"
      });
      return;
    }
    
    // Validar estrutura da geração mensal
    console.log('🔍 Validando estrutura da geração mensal...');
    console.log('  - monthlyGeneration existe:', !!results.monthlyGeneration);
    console.log('  - Tipo de monthlyGeneration:', typeof results.monthlyGeneration);
    console.log('  - Valor de monthlyGeneration:', results.monthlyGeneration);
    
    if (results.monthlyGeneration && typeof results.monthlyGeneration === 'object') {
      const expectedMonths = [
        'janeiro', 'fevereiro', 'marco', 'abril', 'maio', 'junho',
        'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
      ];
      
      console.log('  - Meses esperados:', expectedMonths);
      console.log('  - Meses presentes:', Object.keys(results.monthlyGeneration));
      
      const hasAllMonths = expectedMonths.every(month => 
        typeof results.monthlyGeneration![month] === 'number' && results.monthlyGeneration![month] >= 0
      );
      
      console.log('  - Todos os meses válidos:', hasAllMonths);
      
      if (!hasAllMonths) {
        console.log('❌ Validação da geração mensal falhou');
        toast({
          title: "Geração mensal inválida",
          description: "Dados de geração mensal estão incompletos ou inválidos. Recalcule o sistema.",
          variant: "destructive"
        });
        return;
      }
      
      console.log('✅ Validação da geração mensal: todos os 12 meses presentes e válidos');
    } else {
      console.log('❌ monthlyGeneration não existe ou não é um objeto');
      toast({
        title: "Geração mensal ausente",
        description: "Dados de geração mensal não foram calculados. Recalcule o sistema.",
        variant: "destructive"
      });
      return;
    }
    
    setIsLoading(true);
    try {
      // Determinar a tabela baseada no grupo tarifário
      let targetTable;
      switch (leadData.grupo_tarifario) {
        case 'Grupo A':
          targetTable = 'clientes_grupoa';
          break;
        case 'Grupo B':
          targetTable = 'clientes_grupob';
          break;
        case 'Mercado Livre':
          targetTable = 'clientes_ml';
          break;
        default:
          throw new Error('Grupo tarifário não reconhecido');
      }

      // Calcular o consumo efetivo médio baseado no grupo tarifário
      let consumoEfetivoMedio: number | undefined;
      
      if (leadData.grupo_tarifario === 'Grupo B') {
        // Para Grupo B: usar o consumo médio simples
        if (consumoMedio?.mediaConsumo) {
          consumoEfetivoMedio = consumoMedio.mediaConsumo;
        }
      } else if (leadData.grupo_tarifario === 'Grupo A' || leadData.grupo_tarifario === 'Mercado Livre') {
        // Para Grupo A e Mercado Livre: calcular consumo efetivo com multiplicador
        if (consumoMedio?.dadosMensais && Array.isArray(consumoMedio.dadosMensais)) {
          const multiplier = calculateConsumoEfetivo(consumoMedio.dadosMensais);

          // Calcular consumo efetivo médio mensal
          if (consumoMedio.mediaConsumoForaPonta && consumoMedio.mediaConsumoPonta) {
            consumoEfetivoMedio = consumoMedio.mediaConsumoForaPonta + (consumoMedio.mediaConsumoPonta * multiplier);
          }
        }
      }

      console.log('🔍 Consumo efetivo médio calculado:', consumoEfetivoMedio);

      // Preparar dados para envio - converter para números
      const payload: {
        grupo: string;
        dimensio: number;
        percentual: number;
        eficiencia: number;
        geracao_anual_kwp: number;
        geracao_mensal: { [key: string]: number };
        consumo_efetivo_medio?: number;
        dimensionamento_id: string;
      } = {
        grupo: targetTable,
        dimensio: systemPower,
        percentual: percentual,
        eficiencia: eficiencia,
        geracao_anual_kwp: annualGen,
        geracao_mensal: results.monthlyGeneration,
        dimensionamento_id: dimensionamentoId!
      };

      // Adicionar consumo_efetivo_medio se calculado
      if (consumoEfetivoMedio !== undefined && !isNaN(consumoEfetivoMedio)) {
        payload.consumo_efetivo_medio = consumoEfetivoMedio;
        console.log('✅ Consumo efetivo médio incluído no payload:', consumoEfetivoMedio);
      } else {
        console.log('⚠️ Consumo efetivo médio não pôde ser calculado');
      }

      console.log('🔍 Dados sendo enviados para o backend:', payload);
      console.log('🔍 Tipo do campo geracao_anual_kwp:', typeof payload.geracao_anual_kwp);
      console.log('🔍 Valor do campo geracao_anual_kwp:', payload.geracao_anual_kwp);
      console.log('🔍 Tipo do campo geracao_mensal:', typeof payload.geracao_mensal);
      console.log('🔍 Valor do campo geracao_mensal:', payload.geracao_mensal);
      console.log('🔍 Results object:', results);
      console.log('🔍 Tabela de destino:', targetTable);

      // Salvar o resultado na tabela apropriada
      const response = await fetchWithAuth(`/api/leads/${leadData.id}/save-dimensionamento`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Erro na resposta do servidor:', response.status, errorData);
        throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Marcar dimensionamento como não finalizado quando há alterações
        try {
          const { PropostasService } = await import('@/services/propostasService');
          await PropostasService.marcarDimensionamentoNaoFinalizado(leadData.id, dimensionamentoId);
          console.log('✅ Dimensionamento marcado como não finalizado após alterações na etapa 2');
        } catch (error) {
          console.error('❌ Erro ao marcar dimensionamento como não finalizado:', error);
          // Não falhar o salvamento se houver erro ao marcar como não finalizado
        }

        toast({
          title: "Dimensionamento salvo com sucesso!",
          description: `Potência de ${results.systemPowerKWp} kWp e geração anual de ${results.annualGenPerKwp} kWh/kWp/ano salvos na base de dados.`,
        });
        
        // Marcar como salvo
        setIsSaved(true);
        console.log('✅ Dimensionamento salvo com sucesso para', targetTable);
      } else {
        throw new Error(result.message || 'Erro ao salvar dimensionamento');
      }
      
    } catch (error: unknown) {
      console.error('❌ Erro ao salvar dimensionamento:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      
      // Mensagem de erro mais específica para Grupo A
      if (leadData.grupo_tarifario === 'Grupo A') {
        toast({
          title: "Erro ao salvar dimensionamento para Grupo A",
          description: `Problema específico: ${errorMessage}. Verifique se todos os dados estão corretos e tente novamente.`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Erro ao salvar dimensionamento",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Validação dos campos
  const validateInputs = () => {
    const errors: string[] = [];

    // Validar percentual
    if (!targetPercentage || targetPercentage.trim() === "") {
      errors.push("Percentual de energia é obrigatório");
    } else {
      const percentage = parseFloat(targetPercentage);
      if (isNaN(percentage) || percentage <= 0) {
        errors.push("Percentual deve ser um número positivo");
      } else if (percentage > 200) {
        errors.push("Percentual não pode ser maior que 200%");
      }
    }

    // Validar eficiência
    if (!efficiency || efficiency.trim() === "") {
      errors.push("Eficiência é obrigatória");
    } else {
      const eff = parseFloat(efficiency);
      if (isNaN(eff) || eff <= 0 || eff > 100) {
        errors.push("Eficiência deve ser um número entre 0 e 100");
      }
    }

    // Validações específicas para Grupo A
    if (leadData?.grupo_tarifario === 'Grupo A') {
      if (!leadData.cidade || !leadData.estado) {
        errors.push("Para clientes Grupo A, cidade e estado são obrigatórios para cálculo de irradiação");
      }
      
      if (!leadData.padrao) {
        errors.push("Para clientes Grupo A, o padrão é obrigatório");
      }
    }

    // Validações específicas para Grupo B
    if (leadData?.grupo_tarifario === 'Grupo B') {
      if (!leadData.padrao) {
        errors.push("Para clientes Grupo B, o padrão é obrigatório");
      }
    }

    if (leadData?.padrao === 'Múltiplas Faturas') {
      const v = leadData.fatura_minima_kwh != null ? Number(leadData.fatura_minima_kwh) : NaN;
      if (typeof v !== 'number' || Number.isNaN(v) || v <= 0) {
        errors.push("Para padrão Múltiplas Faturas, informe a Fatura Mínima (kWh) na etapa 1");
      }
    }

    // Validações gerais
    if (!leadData?.id) {
      errors.push("Cliente não selecionado");
    }

    if (!leadData?.grupo_tarifario) {
      errors.push("Grupo tarifário não definido");
    }

    setValidationErrors(errors);
    
    if (errors.length > 0) {
      console.log('❌ Erros de validação encontrados:', errors);
      // Mostrar toast com erros de validação
      toast({
        title: "Erros de validação",
        description: `Corrija os seguintes problemas:\n${errors.join('\n')}`,
        variant: "destructive"
      });
    }
    
    return errors.length === 0;
  };


  // Função para limpar dados das etapas 3 e 4
  const clearEtapasSeguintes = async (leadId: string) => {
    try {
      console.log('🧹 Iniciando limpeza dos dados das etapas 3 e 4 para lead:', leadId, 'dimensionamento:', dimensionamentoId);
      
      // Limpar dados da etapa 3 e 4 (propostas_clientes) apenas para o dimensionamento específico
      const { error: propostasError } = await supabase
        .from('propostas_clientes')
        .update({
          // Dados dos kits (etapa 3)
          kit1_codigo: null,
          kit1_nome: null,
          kit1_potencia: null,
          kit1_preco: null,
          kit1_quantidade: null,
          kit1_insumos: null,
          kit1_potencia_painel: null,
          kit1_quantidade_paineis: null,
          kit1_modulo_descricao: null,
          kit2_codigo: null,
          kit2_nome: null,
          kit2_potencia: null,
          kit2_preco: null,
          kit2_quantidade: null,
          kit2_insumos: null,
          kit2_potencia_painel: null,
          kit2_quantidade_paineis: null,
          kit2_modulo_descricao: null,
          kit3_codigo: null,
          kit3_nome: null,
          kit3_potencia: null,
          kit3_preco: null,
          kit3_quantidade: null,
          kit3_insumos: null,
          kit3_potencia_painel: null,
          kit3_quantidade_paineis: null,
          kit3_modulo_descricao: null,
          kit4_codigo: null,
          kit4_nome: null,
          kit4_potencia: null,
          kit4_preco: null,
          kit4_quantidade: null,
          kit4_insumos: null,
          kit4_potencia_painel: null,
          kit4_quantidade_paineis: null,
          kit4_modulo_descricao: null,
          total_kits: 0,
          valor_total_kits: 0,
          desconto_kits: 0,
          potencia_total_inversores: 0,
          total_paineis: 0,
          kits_r_wp: null,
          // Dados de precificação (etapa 4)
          valor_estrutura_metallica: 0,
          valor_fundacao: 0,
          valor_equipamentos_alugados: 0,
          custo_materiais: 0,
          comissao_vendedor: 0, // Valor padrão
          comissao_parceiro: 5, // Valor padrão
          comissao_vendedor_valor: 0, // Será calculado posteriormente
          comissao_parceiro_valor: 0, // Será calculado posteriormente
          integracao_r_wp: 1, // Valor padrão
          impostos_percentual: 12, // Valor padrão
          mao_obra_por_placa: 0,
          preco_projeto: 0,
          preco_integracao: 0,
          preco_estrutura_metallica_calc: 0,
          comissao: 0,
          impostos: 0,
          mao_obra: 0,
          custo_total_implantacao: 0,
          custo_total_integracao: 0,
          preco_por_wp_final: 0,
          lucro_liquido: 0,
          lucro_liquido_percentual: 0,
          // Dados de análise e proposta
          com_sfv: null,
          geracao_ano1: 0,
          proposta_pdf_url: null,
          dimensionamento_finalizado: false
        })
        .eq('lead_id', leadId)
        .eq('dimensionamento_id', dimensionamentoId);
      
      if (propostasError) {
        console.warn('⚠️ Erro ao limpar propostas_clientes:', propostasError);
      } else {
        console.log('✅ Dados de kits e propostas limpos para dimensionamento específico');
      }
      
      // Limpar dados da etapa 4 (projecao_grupob) apenas para o dimensionamento específico
      const { error: projecaoError } = await supabase
        .from('projecao_grupob')
        .delete()
        .eq('lead_id', leadId)
        .eq('dimensionamento_id', dimensionamentoId);
      
      if (projecaoError) {
        console.warn('⚠️ Erro ao limpar projecao_grupob:', projecaoError);
      } else {
        console.log('✅ Tabela projecao_grupob limpa para dimensionamento específico');
      }
      
      console.log('🎯 Limpeza das etapas seguintes concluída para dimensionamento:', dimensionamentoId);
      
    } catch (error) {
      console.error('❌ Erro ao limpar dados das etapas seguintes:', error);
    }
  };

  // useEffect para detectar mudanças nos campos de percentual e eficiência
  useEffect(() => {
    // Função para detectar mudanças nos campos de percentual e eficiência
    const detectChanges = () => {
      if (!originalTargetPercentage || !originalEfficiency) {
        return false;
      }
      
      const targetPercentageChanged = targetPercentage !== originalTargetPercentage;
      const efficiencyChanged = efficiency !== originalEfficiency;
      
      const hasChanges = targetPercentageChanged || efficiencyChanged;
      
      console.log('🔍 Detecção de mudanças na etapa 2:', {
        targetPercentageChanged,
        efficiencyChanged,
        currentTargetPercentage: targetPercentage,
        originalTargetPercentage,
        currentEfficiency: efficiency,
        originalEfficiency,
        hasChanges
      });
      
      return hasChanges;
    };

    const hasChanges = detectChanges();
    setHasUnsavedChanges(hasChanges);
    
    if (hasChanges) {
      console.log('⚠️ Mudanças detectadas na etapa 2 - dados das etapas 3 e 4 serão limpos ao salvar');
    }
  }, [targetPercentage, efficiency, originalTargetPercentage, originalEfficiency]);

  // useEffect para definir valores originais quando o componente for montado
  useEffect(() => {
    if (targetPercentage && efficiency && !originalTargetPercentage && !originalEfficiency) {
      setOriginalTargetPercentage(targetPercentage);
      setOriginalEfficiency(efficiency);
      console.log('📝 Valores originais definidos:', { targetPercentage, efficiency });
    }
  }, [targetPercentage, efficiency, originalTargetPercentage, originalEfficiency]);

  // Validação para avançar para próxima etapa
  const canProceed = useCallback(() => {
    // Se já foi salvo (incluindo dados restaurados), pode avançar
    if (isSaved || dimensionamentoRestaurado) {
      return true;
    }
    
    // Se foi calculado mas não salvo, precisa salvar primeiro
    if (isCalculated && results) {
      // Verificar se os resultados são válidos
      if (results.systemPowerKWp && results.annualGenPerKwp) {
        const systemPower = parseFloat(results.systemPowerKWp);
        const annualGen = parseFloat(results.annualGenPerKwp);
        
        if (isNaN(systemPower) || systemPower <= 0 || isNaN(annualGen) || annualGen <= 0) {
          return false; // Resultados inválidos
        }
      }
      
      return isSaved; // Precisa estar salvo
    }
    
    return false;
  }, [isSaved, dimensionamentoRestaurado, isCalculated, results]);

  const canSaveDimensionamento = useCallback(() => {
    // Pode salvar se: está calculado, não está salvo, não está restaurado, tem resultados válidos
    return isCalculated && !isSaved && !dimensionamentoRestaurado && results && 
           results.systemPowerKWp && results.annualGenPerKwp &&
           parseFloat(results.systemPowerKWp) > 0 && parseFloat(results.annualGenPerKwp) > 0;
  }, [isCalculated, isSaved, dimensionamentoRestaurado, results]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Dimensionamento do Sistema
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mensagem de dimensionamento restaurado */}
          {dimensionamentoRestaurado && (
            <Alert className="border-green-200 bg-green-50">
              <AlertCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Dimensionamento restaurado!</strong> Os dados salvos anteriormente foram carregados automaticamente.
              </AlertDescription>
            </Alert>
          )}
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Informações do Cliente</h3>
            {isLoadingLead ? (
              <p className="text-blue-600">Carregando dados do cliente...</p>
            ) : leadData ? (
              <div className="text-sm text-blue-700 space-y-1">
                <p><strong>Nome:</strong> {leadData.nome || leadData.razao_social}</p>
                <p><strong>Grupo Tarifário:</strong> {leadData.grupo_tarifario || 'Não definido'}</p>
                <p><strong>Padrão:</strong> {leadData.padrao || 'Não definido'}</p>
                <p><strong>Localização:</strong> {leadData.cidade || 'Não definido'}, {leadData.estado || 'Não definido'}</p>
                
                {/* Informações de Consumo Médio */}
                {consumoMedio && (
                  <div className="mt-3 pt-3 border-t border-blue-300">
                    <p className="font-semibold text-blue-800 mb-2">Consumo Médio Mensal (calculado a partir dos dados históricos):</p>
                    {leadData.grupo_tarifario === 'Grupo B' ? (
                      <p><strong>Consumo Médio:</strong> {consumoMedio.mediaConsumo ? `${consumoMedio.mediaConsumo.toFixed(2)} kWh/mês` : 'Não disponível'}</p>
                    ) : (
                      <div className="space-y-1">
                        <p><strong>Consumo Médio Ponta:</strong> {consumoMedio.mediaConsumoPonta ? `${consumoMedio.mediaConsumoPonta.toFixed(2)} kWh/mês` : 'Não disponível'}</p>
                        <p><strong>Consumo Médio Fora Ponta:</strong> {consumoMedio.mediaConsumoForaPonta ? `${consumoMedio.mediaConsumoForaPonta.toFixed(2)} kWh/mês` : 'Não disponível'}</p>
                        {consumoMedio.mediaConsumoPonta && consumoMedio.mediaConsumoForaPonta && (() => {
                          const multiplier = calculateConsumoEfetivo(consumoMedio.dadosMensais);
                          const consumoEfetivo = consumoMedio.mediaConsumoForaPonta + (consumoMedio.mediaConsumoPonta * multiplier);

                          return (
                            <p className="text-blue-600 font-medium">
                              <strong>Consumo Efetivo Médio:</strong> {consumoEfetivo.toFixed(2)} kWh/mês
                            </p>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-red-600">Nenhum cliente selecionado</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="targetPercentage">
                Percentual de Energia a Ser Gerada (%) *
              </Label>
              <Input
                id="targetPercentage"
                type="number"
                value={targetPercentage}
                onChange={(e) => setTargetPercentage(e.target.value)}
                placeholder="Ex: 100, 80, 120"
                step="0.01"
                min="0"
                className={validationErrors.includes("Percentual de energia é obrigatório") ? "border-red-500" : ""}
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Digite qualquer valor (ex: 100 para 100%, 120 para 120%)
              </p>
            </div>

                         <div>
               <Label htmlFor="efficiency">
                 Eficiência do Sistema (%) *
               </Label>
               <Input
                 id="efficiency"
                 type="number"
                 value={efficiency}
                 onChange={(e) => setEfficiency(e.target.value)}
                 placeholder="74.0"
                 step="0.1"
                 min="0"
                 max="100"
                 className={validationErrors.includes("Eficiência é obrigatória") ? "border-red-500" : ""}
                 disabled={readOnly}
               />
               <p className="text-xs text-muted-foreground mt-1">
                 Valor padrão: 74,0% (igual à Calculadora)
               </p>
             </div>
          </div>

          {validationErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {!readOnly && (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={calculateDimensionamento} 
                disabled={isLoading || !leadData || isLoadingLead}
                className="w-full sm:flex-1"
              >
                {isLoading ? "Calculando..." : "Calcular Dimensionamento"}
              </Button>
              
              {canSaveDimensionamento() && (
                <Button 
                  onClick={saveDimensionamento}
                  variant="outline"
                  disabled={isLoading}
                  className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                >
                  {isLoading ? "Salvando..." : "Salvar Dimensionamento"}
                </Button>
              )}

              {(isCalculated || dimensionamentoRestaurado) && (
                <Button 
                  onClick={clearResults}
                  variant="outline"
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  Limpar Resultado
                </Button>
              )}
            </div>
          )}

          {(results || dimensionamentoRestaurado) && (
            <Card className="border border-green-200 bg-green-50">
              <CardHeader className="pb-3">
                <CardTitle className="text-green-800 text-lg">
                  Resultado do Dimensionamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <Zap className="h-8 w-8 text-yellow-500" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Potência do Sistema</p>
                      <p className="text-2xl font-bold text-green-800">
                        {results?.systemPowerKWp || (dimensionamentoRestaurado ? 'Carregado' : 'N/A')} kWp
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                    <TrendingUp className="h-8 w-8 text-purple-500" />
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Geração Anual</p>
                      <p className="text-2xl font-bold text-green-800">
                        {results?.annualGenPerKwp || (dimensionamentoRestaurado ? 'Carregado' : 'N/A')} kWh/kWp/ano
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Resumo:</strong> Sistema dimensionado para gerar {targetPercentage}% da energia necessária 
                    com eficiência de {efficiency}%.{' '}
                    {results ? (
                      <>A potência calculada de {results.systemPowerKWp} kWp 
                      considera as condições de irradiação da localidade e gera aproximadamente {results.annualGenPerKwp} kWh por kWp por ano.</>
                    ) : (
                      <>Os dados de dimensionamento foram carregados da base de dados.</>
                    )}
                  </p>
                  
                  {isSaved && (
                    <div className="mt-3 pt-3 border-t border-blue-300">
                      <p className="text-sm text-green-700 font-medium">
                        {dimensionamentoRestaurado 
                          ? "✅ Dimensionamento restaurado da base de dados!" 
                          : "✅ Dimensionamento salvo com sucesso na base de dados!"
                        }
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-between pt-4">
            <Button onClick={onBack} variant="outline">
              Voltar
            </Button>
            <div className="flex flex-col items-end gap-2">
              {!canProceed() && (
                <p className="text-sm text-muted-foreground">
                  {!isCalculated ? "Calcule o dimensionamento primeiro" : 
                   isCalculated && !isSaved ? "Salve o dimensionamento para continuar" : 
                   "Complete todos os campos obrigatórios"}
                </p>
              )}
              <Button 
                onClick={() => onNext(dimensionamentoId || undefined)} 
                disabled={!canProceed() || readOnly}
                className="bg-green-600 hover:bg-green-700"
                title={readOnly ? "Perfil parceiro: apenas visualização permitida" : undefined}
              >
                {readOnly ? "Apenas Visualização" : (!canProceed() ? "Salvar Primeiro" : "Próximo")}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
